import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

export interface FolderNode {
	id: number;
	name: string;
	icon?: string;
	children: FolderNode[];
}

/** Accessor type can be a string key or a function */
export type Accessor<TValue> = string | ((item: any) => TValue);

@Component({
	selector: 'maj-folder-tree-view',
	standalone: true,
	imports: [CommonModule],
	templateUrl: './folder-tree-view.component.html',
	styleUrls: ['./folder-tree-view.component.css'],
})
export class FolderTreeViewComponent implements OnInit, OnChanges {

	WHITE = '#ffffff';
	BLACK = '#000000';

	/** The tree nodes to render Use this if you want to build the tree yourself.*/
	@Input()
	nodes: FolderNode[] = [];

	/** The flat list of nodes to render. Use this if you want to render a flat list of nodes. If you use nodes, this will be ignored.*/
	@Input()
	flatNodes: any[] = [];

	@Input()
	idAccessor: Accessor<number> = 'id';

	@Input()
	nameAccessor: Accessor<string> = 'name';

	@Input()
	parentAccessor: Accessor<number | null | undefined> = 'parentId';

	@Input()
	iconAccessor: Accessor<string | undefined> = 'icon';

	@Input()
	initiallyExpandedIds: number[] = [];

	@Input()
	selectedId: number | null = null;

	@Input()
	primaryColor?: string = this.BLACK;

	@Input()
	secondaryColor?: string = this.WHITE;

	@Output()
	nodeSelected = new EventEmitter<FolderNode>();

	@Output()
	nodeToggled = new EventEmitter<{ node: FolderNode, expanded: boolean }>();

	expanded = new Set<number>();

	/** Holds the built tree when using flatNodes */
	private builtTree: FolderNode[] = [];

	ngOnInit(): void {
		if (Array.isArray(this.initiallyExpandedIds)) {
			for (const id of this.initiallyExpandedIds) {
				this.expanded.add(id);
			}
		}
		if (this.flatNodes && this.flatNodes.length > 0) {
			this.builtTree = this.buildTreeFromFlat(this.flatNodes);
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['initiallyExpandedIds'] && Array.isArray(this.initiallyExpandedIds)) {
			for (const id of this.initiallyExpandedIds) {
				this.expanded.add(id);
			}
		}


		if (changes['flatNodes'] || changes['idAccessor'] || changes['nameAccessor'] || changes['parentAccessor'] || changes['iconAccessor']) {
			this.builtTree = this.buildTreeFromFlat(this.flatNodes ?? []);
		}
	}

	get nodesToRender(): FolderNode[] {
		return (this.nodes && this.nodes.length > 0) ? this.nodes : this.builtTree;
	}

	hasChildren(node: FolderNode | undefined | null): boolean {
		return !!node && Array.isArray(node.children) && node.children.length > 0;
	}

	isExpanded(node: FolderNode): boolean {
		return this.expanded.has(node.id);
	}

	toggle(node: FolderNode): void {
		if (!this.hasChildren(node)) return;
		const nowExpanded = !this.expanded.has(node.id);
		if (nowExpanded) {
			this.expanded.add(node.id);
		} else {
			this.expanded.delete(node.id);
		}
		this.nodeToggled.emit({node, expanded: nowExpanded});
	}

	select(node: FolderNode): void {
		this.selectedId = node.id;
		this.nodeSelected.emit(node);
	}

	private getValue<T>(item: any, accessor: Accessor<T>): T {
		return typeof accessor === 'function' ? accessor(item) : item?.[accessor as keyof typeof item];
	}

	private buildTreeFromFlat(items: any[]): FolderNode[] {
		const nodeById = new Map<number, FolderNode>();
		const roots: FolderNode[] = [];
		const waitingChildren = new Map<number, FolderNode[]>();

		for (const src of items) {
			const id = this.getValue<number>(src, this.idAccessor);
			const name = this.getValue<string>(src, this.nameAccessor);
			const icon = this.getValue<string | undefined>(src, this.iconAccessor);
			const parentId = this.getValue<number | null | undefined>(src, this.parentAccessor);
			if (id == null) {
				continue;
			}

			const node: FolderNode = {id, name, icon, children: []};
			nodeById.set(id, node);

			if (parentId == null || parentId === id) {
				roots.push(node);
			} else {
				const parent = nodeById.get(parentId);
				if (parent) {
					parent.children.push(node);
				} else {
					const pending = waitingChildren.get(parentId) ?? [];
					pending.push(node);
					waitingChildren.set(parentId, pending);
				}
			}

			const waiting = waitingChildren.get(id);
			if (waiting) {
				for (const child of waiting) {
					node.children.push(child);
				}
				waitingChildren.delete(id);
			}
		}

		// Any remaining orphans whose parents never appeared become roots
		return roots.concat(Array.from(waitingChildren.values()).reduce((acc, arr) => acc.concat(arr), [] as FolderNode[]));
	}

	getRowStyles(node: FolderNode): { [key: string]: string } {
		const primary = (this.primaryColor ?? '').trim();
		const secondary = (this.secondaryColor ?? '').trim();
		if (!primary || !secondary) {
			return {};
		}
		const selected = this.selectedId === node.id;
		return {
			'background-color': selected ? primary : this.WHITE,
			'color': selected ? secondary : this.BLACK,
			'font-weight': selected ? 'bold' : 'normal',
		};
	}
}
